import { Link } from "react-router-dom";
import { durationParts } from "../../lib/dashboard";
import { count, kwh } from "../../lib/format";
import type { UsageStats } from "../../lib/types";
import { MicroLabel, SectionTitle } from "../ui/Text";
import { Figure } from "./Figure";
import { PanelLink } from "./PanelLink";

/**
 * Charging in the window: how much, split between the fleet's own cards and public
 * drivers, and which stations delivered it.
 *
 * Closed sessions only, attributed by when they ended — the same rule as the
 * fleet and depot energy columns, so a figure here reconciles with one there.
 */
export function UsagePanel({
  usage,
  label,
}: { readonly usage: UsageStats; readonly label: string }) {
  const { totals } = usage;
  const duration = durationParts(totals.avgDurationS);
  const busiest = usage.topStations[0]?.energyWh ?? 0;

  return (
    <section aria-labelledby="usage-title" className="flex flex-col gap-4">
      <SectionTitle action={<PanelLink to="/sessions">Sessions</PanelLink>}>
        <span id="usage-title">Charging · {label}</span>
      </SectionTitle>

      <dl className="m-0 grid grid-cols-2 gap-4 md:grid-cols-4">
        <Figure
          label="Fleet cards"
          value={kwh(totals.fleetEnergyWh)}
          unit="kWh"
          sub={sessionsLabel(totals.fleetSessions)}
        />
        <Figure
          label="Public"
          value={kwh(totals.publicEnergyWh)}
          unit="kWh"
          sub={sessionsLabel(totals.publicSessions)}
        />
        <Figure label="Avg session" value={duration.value} unit={duration.unit} />
        <Figure label="Avg energy" value={kwh(totals.avgEnergyWh)} unit="kWh" />
      </dl>

      <div className="flex flex-col gap-2">
        <MicroLabel>Top stations</MicroLabel>
        {usage.topStations.length === 0 ? (
          <p className="m-0 text-body text-ink-muted">No sessions closed in this window.</p>
        ) : (
          <ol className="m-0 flex list-none flex-col p-0">
            {usage.topStations.map((station) => (
              <li
                key={station.chargePointId}
                className="grid grid-cols-[minmax(0,10rem)_1fr_auto] items-center gap-3 py-1"
              >
                <Link
                  to={`/fleet/${encodeURIComponent(station.chargePointId)}`}
                  className="truncate text-data text-ink underline decoration-rule underline-offset-4 hover:decoration-ink"
                >
                  {station.chargePointId}
                </Link>
                {/* Relative to the busiest, which is what a ranking is for. */}
                <span className="h-1.5 bg-paper-sunken" aria-hidden="true">
                  <span
                    className="block h-full bg-ink"
                    style={{ width: `${busiest === 0 ? 0 : (station.energyWh / busiest) * 100}%` }}
                  />
                </span>
                <span className="text-data text-ink tabular-nums">
                  {kwh(station.energyWh)} kWh · {count(station.sessions)}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}

function sessionsLabel(sessions: number): string {
  return `${count(sessions)} ${sessions === 1 ? "session" : "sessions"}`;
}
